<template>
   <dashboard-layout>
      <v-container fluid class="down-top-padding py-0">
         <BaseBreadcrumb :title="page.title" :breadcrumbs="breadcrumbs"></BaseBreadcrumb>

         <v-card flat class="external-payment-card">
            <v-card-title class="external-payment-header">
               <div>
                  <div class="text-h6">Record External Payment</div>
                  <div class="header-copy">Reconcile a verified payment received outside the system without generating a duplicate receipt.</div>
               </div>
               <v-spacer></v-spacer>
               <v-btn text color="secondary" :disabled="saving" @click="resetForm">Reset</v-btn>
            </v-card-title>

            <v-divider></v-divider>

            <v-card-text class="external-payment-body">
               <v-alert dense text type="info" class="mb-5">
                  Use this form only when MIP has already received the payment and issued the official receipt manually.
               </v-alert>

               <section class="form-section">
                  <div class="section-heading">1. Select Member</div>
                  <v-autocomplete
                     v-model="selectedMember"
                     :items="members"
                     :loading="loadingMembers"
                     item-text="fullname"
                     item-value="userid"
                     return-object
                     outlined
                     dense
                     clearable
                     label="Member"
                     placeholder="Search by name, Member ID, IC/Passport or email"
                     :filter="memberFilter"
                     @change="loadOutstanding"
                  >
                     <template v-slot:item="{ item }">
                        <v-list-item-content>
                           <v-list-item-title>{{ item.fullname }}</v-list-item-title>
                           <v-list-item-subtitle>
                              {{ item.memberid || 'No Member ID' }} - {{ item.icpassport || 'No IC/Passport' }} - {{ item.membertype }}
                           </v-list-item-subtitle>
                        </v-list-item-content>
                     </template>
                  </v-autocomplete>
               </section>

               <section v-if="selectedMember" class="form-section">
                  <div class="section-heading">2. Allocate Payment</div>

                  <div v-if="loadingOutstanding" class="loading-state">
                     <v-progress-circular indeterminate color="primary" size="24"></v-progress-circular>
                     <span>Loading outstanding years...</span>
                  </div>

                  <template v-else-if="outstanding.years.length">
                     <div class="outstanding-summary">
                        <div>
                           <span>Total Outstanding</span>
                           <strong>RM {{ money(outstanding.amount) }}</strong>
                        </div>
                        <div>
                           <span>Annual Fee</span>
                           <strong>RM {{ money(outstanding.annual_fee) }}</strong>
                        </div>
                        <div>
                           <span>Selected Payment</span>
                           <strong class="selected-total">RM {{ money(selectedTotal) }}</strong>
                        </div>
                        <div>
                           <span>Balance After Payment</span>
                           <strong>RM {{ money(remainingTotal) }}</strong>
                        </div>
                     </div>

                     <v-simple-table class="year-allocation-table">
                        <template v-slot:default>
                           <thead>
                              <tr>
                                 <th class="selection-column">Select</th>
                                 <th>Outstanding Year</th>
                                 <th class="text-right">Annual Fee</th>
                                 <th>Status</th>
                              </tr>
                           </thead>
                           <tbody>
                              <tr v-for="(year, index) in outstanding.years" :key="year">
                                 <td>
                                    <v-checkbox
                                       :input-value="selectedYears.includes(year)"
                                       :disabled="!canToggleYear(index)"
                                       hide-details
                                       color="#14532d"
                                       @change="toggleYear(year, index, $event)"
                                    ></v-checkbox>
                                 </td>
                                 <td><strong>{{ year }}</strong></td>
                                 <td class="text-right">RM {{ money(outstanding.annual_fee) }}</td>
                                 <td>
                                    <v-chip v-if="selectedYears.includes(year)" x-small dark color="#14532d">Included</v-chip>
                                    <span v-else class="pending-label">Outstanding</span>
                                 </td>
                              </tr>
                           </tbody>
                        </template>
                     </v-simple-table>

                     <div class="allocation-note">
                        Payment must be allocated from the oldest outstanding year first. A year is settled only when its full annual fee is covered.
                     </div>
                  </template>

                  <v-alert v-else dense text type="success" class="mb-0">
                     This member has no outstanding annual subscription fee.
                  </v-alert>
               </section>

               <section v-if="selectedMember && outstanding.years.length" class="form-section">
                  <div class="section-heading">3. Confirm External Receipt</div>
                  <v-text-field
                     v-model="paymentDate"
                     type="date"
                     outlined
                     dense
                     label="Payment Date"
                     class="payment-date-field"
                  ></v-text-field>

                  <div class="receipt-confirmation">
                     <v-checkbox
                        v-model="receiptConfirmed"
                        color="#14532d"
                        hide-details
                        label="I confirm that MIP received this payment and the official receipt was issued manually outside the system."
                     ></v-checkbox>
                     <p>No new MIP receipt number or system receipt will be generated.</p>
                  </div>
               </section>

               <v-alert v-if="message" :type="messageType" dense text class="mt-4 mb-0">
                  {{ message }}
               </v-alert>
            </v-card-text>

            <v-card-actions class="external-payment-actions">
               <v-spacer></v-spacer>
               <v-btn text href="/admin/payments">Cancel</v-btn>
               <v-btn
                  color="#14532d"
                  dark
                  depressed
                  :loading="saving"
                  :disabled="!canSubmit"
                  @click="submitExternalPayment"
               >
                  <v-icon left small>mdi-check-circle</v-icon>
                  Confirm External Payment
               </v-btn>
            </v-card-actions>
         </v-card>
      </v-container>
   </dashboard-layout>
</template>

<script>
import BaseBreadcrumb from '@/components/BaseBreadcrumb.vue'
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
const axios = require('axios')

export default {
   name: 'MOAExternalPayment',
   components: { BaseBreadcrumb, DashboardLayout },
   data() {
      return {
         page: { title: 'External Payment' },
         breadcrumbs: [
            { text: 'Dashboard', disabled: false, to: '/dashboard' },
            { text: 'Payments', disabled: false, to: '/admin/payments' },
            { text: 'External Payment', disabled: true },
         ],
         members: [],
         selectedMember: null,
         selectedYears: [],
         outstanding: { years: [], annual_fee: 0, amount: 0 },
         paymentDate: this.localDate(),
         receiptConfirmed: false,
         loadingMembers: false,
         loadingOutstanding: false,
         saving: false,
         message: '',
         messageType: 'info',
      }
   },
   computed: {
      selectedTotal() {
         return this.selectedYears.length * Number(this.outstanding.annual_fee || 0)
      },
      remainingTotal() {
         return Math.max(0, Number(this.outstanding.amount || 0) - this.selectedTotal)
      },
      canSubmit() {
         return !this.saving
            && !!this.selectedMember
            && this.selectedYears.length > 0
            && !!this.paymentDate
            && this.receiptConfirmed
      },
   },
   methods: {
      localDate() {
         const date = new Date()
         const year = date.getFullYear()
         const month = String(date.getMonth() + 1).padStart(2, '0')
         const day = String(date.getDate()).padStart(2, '0')
         return `${year}-${month}-${day}`
      },
      money(value) {
         return Number(value || 0).toFixed(2)
      },
      memberFilter(item, queryText) {
         const q = String(queryText || '').toLowerCase()
         return [item.fullname, item.memberid, item.icpassport, item.contactemail]
            .join(' ')
            .toLowerCase()
            .includes(q)
      },
      canToggleYear(index) {
         if (this.selectedYears.includes(this.outstanding.years[index])) {
            return index === this.selectedYears.length - 1
         }
         return index === this.selectedYears.length
      },
      toggleYear(year, index, checked) {
         if (checked && index === this.selectedYears.length) {
            this.selectedYears.push(year)
         } else if (!checked && index === this.selectedYears.length - 1) {
            this.selectedYears.pop()
         }
      },
      async loadMembers() {
         this.loadingMembers = true
         try {
            const response = await axios.get('/admin/members/list')
            this.members = response.data && Array.isArray(response.data.data) ? response.data.data : []
         } finally {
            this.loadingMembers = false
         }
      },
      async loadOutstanding(preserveMessage = false) {
         this.selectedYears = []
         this.receiptConfirmed = false
         if (!preserveMessage) this.message = ''
         this.outstanding = { years: [], annual_fee: 0, amount: 0 }
         if (!this.selectedMember) return

         this.loadingOutstanding = true
         try {
            const response = await axios.get(`/admin/payments/external/outstanding/${this.selectedMember.userid}`)
            if (response.data && response.data.status === 'OK') {
               this.outstanding = response.data.data.outstanding || this.outstanding
            } else {
               this.messageType = 'error'
               this.message = (response.data && response.data.error) || 'Unable to load outstanding years.'
            }
         } catch (error) {
            this.messageType = 'error'
            this.message = 'Unable to load outstanding years.'
         } finally {
            this.loadingOutstanding = false
         }
      },
      resetForm() {
         this.selectedMember = null
         this.selectedYears = []
         this.outstanding = { years: [], annual_fee: 0, amount: 0 }
         this.paymentDate = this.localDate()
         this.receiptConfirmed = false
         this.message = ''
      },
      async submitExternalPayment() {
         if (!this.canSubmit) return
         this.saving = true
         this.message = ''
         try {
            const response = await axios.post('/admin/payments/external', {
               userid: this.selectedMember.userid,
               years: this.selectedYears,
               paymentdate: this.paymentDate,
               manual_receipt_confirmed: this.receiptConfirmed,
            })

            if (response.data && response.data.status === 'OK') {
               const data = response.data.data || {}
               this.messageType = 'success'
               this.message = `${response.data.message} Recorded RM ${this.money(data.amount_recorded)}. Remaining outstanding: RM ${this.money(data.remaining_amount)}.`
               await this.loadOutstanding(true)
            } else {
               this.messageType = 'error'
               this.message = (response.data && response.data.error) || 'Unable to record external payment.'
            }
         } catch (error) {
            this.messageType = 'error'
            this.message = 'Unable to record external payment. Please try again.'
         } finally {
            this.saving = false
         }
      },
   },
   mounted() {
      this.loadMembers()
   },
}
</script>

<style scoped>
.external-payment-card {
   border: 1px solid #dbe4ee;
   border-radius: 4px;
}

.external-payment-header {
   padding: 18px 22px;
}

.header-copy {
   color: #64748b;
   font-size: 13px;
   margin-top: 3px;
}

.external-payment-body {
   padding: 22px;
}

.form-section {
   border-top: 1px solid #e2e8f0;
   padding: 22px 0;
}

.form-section:first-of-type {
   border-top: 0;
   padding-top: 0;
}

.section-heading {
   color: #0f172a;
   font-size: 14px;
   font-weight: 700;
   margin-bottom: 14px;
}

.loading-state {
   align-items: center;
   color: #475569;
   display: flex;
   gap: 10px;
   min-height: 72px;
}

.outstanding-summary {
   border: 1px solid #cbd5e1;
   display: grid;
   grid-template-columns: repeat(4, minmax(0, 1fr));
   margin-bottom: 18px;
}

.outstanding-summary > div {
   border-right: 1px solid #cbd5e1;
   padding: 14px 16px;
}

.outstanding-summary > div:last-child {
   border-right: 0;
}

.outstanding-summary span,
.outstanding-summary strong {
   display: block;
}

.outstanding-summary span {
   color: #64748b;
   font-size: 11px;
   font-weight: 700;
   margin-bottom: 5px;
   text-transform: uppercase;
}

.outstanding-summary strong {
   color: #0f172a;
   font-size: 17px;
}

.outstanding-summary .selected-total {
   color: #14532d;
}

.year-allocation-table {
   border: 1px solid #dbe4ee;
}

.selection-column {
   width: 90px;
}

.pending-label {
   color: #9a3412;
   font-size: 12px;
   font-weight: 600;
}

.allocation-note {
   color: #64748b;
   font-size: 12px;
   line-height: 1.5;
   margin-top: 10px;
}

.payment-date-field {
   max-width: 280px;
}

.receipt-confirmation {
   border: 1px solid #86a894;
   padding: 14px 16px;
}

.receipt-confirmation ::v-deep .v-label {
   color: #0f172a;
   font-size: 14px;
   font-weight: 600;
}

.receipt-confirmation p {
   color: #64748b;
   font-size: 12px;
   margin: 7px 0 0 32px;
}

.external-payment-actions {
   border-top: 1px solid #dbe4ee;
   padding: 14px 22px;
}

@media (max-width: 760px) {
   .outstanding-summary {
      grid-template-columns: 1fr 1fr;
   }

   .outstanding-summary > div:nth-child(2) {
      border-right: 0;
   }

   .outstanding-summary > div:nth-child(-n+2) {
      border-bottom: 1px solid #cbd5e1;
   }
}
</style>
